import { Link, router } from "@inertiajs/react"
import { Loader2, LogIn, Mic, Send, X } from "lucide-react"
import { useCallback, useState } from "react"

import { AudioUploader } from "@/components/editorial/AudioUploader"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { jsonFetch } from "@/lib/fetch-utils"
import { getInitials } from "@/lib/format-utils"
import { cn } from "@/lib/utils"
import type { CommentUser } from "@/types/phrase"

interface CommentFormProps {
  phraseSlug: string
  currentUser?: CommentUser | null
  className?: string
}

interface PresignResponse {
  url: string
  key: string
  expires_in: number
}

/** Upload file to R2 via presigned URL, return the R2 key */
async function uploadAudioToR2(file: File): Promise<string> {
  // 1. Get presigned URL
  const presign = await jsonFetch<PresignResponse>("/r2/presign_upload", {
    method: "POST",
    body: JSON.stringify({
      filename: file.name,
      content_type: file.type,
      size: file.size,
    }),
  })

  // 2. PUT directly to R2
  const putRes = await fetch(presign.url, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  })

  if (!putRes.ok) {
    throw new Error("Failed to upload audio")
  }

  return presign.key
}

/** Get audio duration in seconds from a File */
function getAudioDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    const audio = new Audio()
    const url = URL.createObjectURL(file)
    audio.addEventListener("loadedmetadata", () => {
      const duration = isFinite(audio.duration) ? audio.duration : 0
      URL.revokeObjectURL(url)
      resolve(Math.round(duration))
    })
    audio.addEventListener("error", () => {
      URL.revokeObjectURL(url)
      resolve(0)
    })
    audio.src = url
  })
}

export function CommentForm({
  phraseSlug,
  currentUser,
  className,
}: CommentFormProps) {
  const [body, setBody] = useState("")
  const [audioFile, setAudioFile] = useState<File | null>(null)
  const [showAudioUploader, setShowAudioUploader] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const hasContent = body.trim().length > 0 || audioFile !== null
  const canSubmit = hasContent && !submitting

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!canSubmit) return

      setSubmitting(true)
      setError(null)

      try {
        let audioR2Key: string | undefined
        let durationSeconds: number | undefined

        // Upload audio to R2 if present
        if (audioFile) {
          ;[audioR2Key, durationSeconds] = await Promise.all([
            uploadAudioToR2(audioFile),
            getAudioDuration(audioFile),
          ])
        }

        // Create the comment
        const payload: Record<string, unknown> = {}
        const trimmedBody = body.trim()
        if (trimmedBody) payload.body = trimmedBody
        if (audioR2Key) {
          payload.audio_r2_key = audioR2Key
          if (durationSeconds != null && durationSeconds > 0) {
            payload.duration_seconds = durationSeconds
          }
        }

        await jsonFetch(`/phrases/${phraseSlug}/comments`, {
          method: "POST",
          body: JSON.stringify(payload),
        })

        // Success — reset form and reload Inertia page to show new comment
        setBody("")
        setAudioFile(null)
        setShowAudioUploader(false)
        router.reload({ preserveScroll: true })
      } catch (err) {
        if (err instanceof Error && err.name !== "AbortError") {
          setError(err.message)
        }
      } finally {
        setSubmitting(false)
      }
    },
    [body, audioFile, phraseSlug, canSubmit],
  )

  // Unauthenticated: show sign-in CTA
  if (!currentUser) {
    return (
      <div
        className={cn(
          "flex items-center gap-4 rounded-xl border border-dashed border-slate-300 bg-slate-50/50 p-5 dark:border-slate-700 dark:bg-slate-800/30",
          className,
        )}
      >
        <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-slate-200 dark:bg-slate-700">
          <LogIn className="size-5 text-slate-500 dark:text-slate-400" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Join the conversation
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            <Link
              href="/sign_in"
              className="font-medium text-indigo-600 underline-offset-2 hover:underline dark:text-indigo-400"
            >
              Sign in
            </Link>
            {" or "}
            <Link
              href="/sign_up"
              className="font-medium text-indigo-600 underline-offset-2 hover:underline dark:text-indigo-400"
            >
              create an account
            </Link>
            {" to comment"}
          </p>
        </div>
      </div>
    )
  }

  return (
    <form
      onSubmit={(e) => void handleSubmit(e)}
      className={cn("space-y-3", className)}
    >
      <div className="flex gap-3">
        {/* Current user avatar */}
        <Avatar className="mt-1 size-9 shrink-0">
          <AvatarFallback
            className={cn(
              "text-xs font-semibold",
              currentUser.role === "teacher"
                ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300"
                : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
            )}
          >
            {getInitials(currentUser.name)}
          </AvatarFallback>
        </Avatar>

        <div className="min-w-0 flex-1 space-y-2">
          {/* Error banner */}
          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}

          {/* Textarea */}
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Share your thoughts or pronunciation tips..."
            maxLength={1000}
            rows={3}
            className="resize-none border-slate-200 bg-white text-sm placeholder:text-slate-400 focus-visible:ring-indigo-500 dark:border-slate-700 dark:bg-slate-900"
          />

          {/* Audio uploader (toggled) */}
          {showAudioUploader && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/50">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                  Record or upload audio
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-6"
                  onClick={() => {
                    setShowAudioUploader(false)
                    setAudioFile(null)
                  }}
                >
                  <X className="size-3.5" />
                </Button>
              </div>
              <AudioUploader
                value={audioFile}
                onChange={setAudioFile}
                maxSizeMb={5}
              />
            </div>
          )}

          {/* Actions row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {!showAudioUploader && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAudioUploader(true)}
                  className="gap-1.5 text-xs text-slate-500 hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400"
                >
                  <Mic className="size-3.5" />
                  Add audio
                </Button>
              )}
              {body.length > 800 && (
                <span className="text-xs text-slate-400">
                  {body.length}/1000
                </span>
              )}
            </div>

            <Button
              type="submit"
              size="sm"
              disabled={!canSubmit}
              className="gap-1.5 bg-indigo-600 text-white shadow-sm hover:bg-indigo-700 disabled:opacity-40"
            >
              {submitting ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Send className="size-3.5" />
              )}
              Comment
            </Button>
          </div>
        </div>
      </div>
    </form>
  )
}
